"use client";

import Script from "next/script";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

export default function GoogleAnalytics({
  GA_MEASUREMENT_ID,
}: {
  GA_MEASUREMENT_ID: string;
}) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Defer analytics loading until after page is interactive
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 1000); // Load analytics 1 second after mount

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isLoaded || typeof window === "undefined" || !window.gtag) return;

    // Check for existing consent
    const consentStatus = document.cookie
      .split("; ")
      .find((row) => row.startsWith("cookie-consent-status="));

    const hasAnalyticsConsent = consentStatus
      ? JSON.parse(decodeURIComponent(consentStatus.split("=")[1])).analytics
      : false;

    const url = pathname + searchParams.toString();

    // Handle consent through dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "update_consent",
      analytics_storage: hasAnalyticsConsent ? "granted" : "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      functionality_storage: "denied",
      personalization_storage: "denied",
      security_storage: "granted",
    });

    // Push to dataLayer with additional configurations for cookie-less tracking
    window.gtag("config", GA_MEASUREMENT_ID, {
      page_path: url,
      cookie_flags: "SameSite=None;Secure;Partitioned",
      cookie_domain: "auto",
      cookie_expires: 2592000, // 30 days
      cookie_prefix: "_ga",
      cookie_update: hasAnalyticsConsent,
      anonymize_ip: true,
      send_page_view: true,
      client_storage: hasAnalyticsConsent ? "cookies" : "none",
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      restricted_data_processing: true,
      storage: hasAnalyticsConsent ? "granted" : "denied",
    });
  }, [pathname, searchParams, GA_MEASUREMENT_ID, isLoaded]);

  if (!isLoaded) return null;

  return (
    <>
      <Script
        strategy='lazyOnload'
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
      />
      <Script
        id='google-analytics'
        strategy='lazyOnload'
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());

            // Initial consent state through dataLayer
            window.dataLayer.push({
              event: "default_consent",
              analytics_storage: "denied",
              ad_storage: "denied",
              ad_user_data: "denied",
              ad_personalization: "denied",
              functionality_storage: "denied",
              personalization_storage: "denied",
              security_storage: "granted"
            });

            gtag('config', '${GA_MEASUREMENT_ID}', {
              cookie_flags: 'SameSite=None;Secure;Partitioned',
              cookie_domain: 'auto',
              cookie_expires: 2592000,
              cookie_prefix: "_ga",
              cookie_update: false,
              anonymize_ip: true,
              client_storage: 'none',
              allow_google_signals: false,
              allow_ad_personalization_signals: false,
              restricted_data_processing: true
            });
          `,
        }}
      />
    </>
  );
}
